import { startTransition, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MedianBoardData } from "../../shared/types";
import { StaticDataState } from "../components/StaticDataState";
import { fetchMedianBoard } from "../lib/static-data";
import { useInfiniteResults } from "../lib/use-infinite-results";

const INITIAL_VISIBLE_COURSES = 48;
const LOAD_MORE_STEP = 72;

export function CoursesByMedianPage() {
  const [medianBoard, setMedianBoard] = useState<MedianBoardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setError(null);
    void fetchMedianBoard().then((nextBoard) => {
      startTransition(() => {
        setMedianBoard(nextBoard);
      });
    }).catch((nextError) => {
      setError(nextError instanceof Error ? nextError.message : "Could not load the median board.");
    });
  }, [reloadKey]);

  const {
    anchorRef,
    hasMore,
    loadMore,
    visibleCount,
    visibleItems,
  } = useInfiniteResults(medianBoard?.rankedCourses ?? [], INITIAL_VISIBLE_COURSES, LOAD_MORE_STEP);

  if (error) {
    return <StaticDataState title="The median board could not load." message={error} error onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (!medianBoard) {
    return <StaticDataState title="Loading the median board..." />;
  }

  if (!medianBoard.currentQuarter) {
    return (
      <section className="panel stack-lg">
        <div className="section-heading">
          <p className="eyebrow">Courses by median</p>
          <h1>Current quarter median board</h1>
        </div>
        <p className="notice-banner">Current quarter offering data is not available yet.</p>
      </section>
    );
  }

  return (
    <section className="stack-lg">
      <div className="panel stack-md">
        <div className="section-heading">
          <p className="eyebrow">Courses by median</p>
          <h1>This Quarter's Courses by Median</h1>
        </div>
        <p className="lede hero-lede">
          These are courses offered in {medianBoard.currentQuarter}, ordered by the typical median reported on Layup List over the past 5 years.
        </p>
        <p className="notice-banner">
          Reported medians come from users of Layup List, so they may be inaccurate. Course medians should not be your main way of choosing classes.
        </p>
        <dl className="median-facts">
          <div>
            <dt>{medianBoard.currentQuarter}</dt>
            <dd>current quarter</dd>
          </div>
          <div>
            <dt>{medianBoard.coursesWithMedian}</dt>
            <dd>with a reported median</dd>
          </div>
          <div>
            <dt>{medianBoard.rankedCourses.length}</dt>
            <dd>courses offered</dd>
          </div>
        </dl>
      </div>

      <div className="panel stack-md">
        <div className="median-course-table">
          {visibleItems.map((course) => {
            const hasMedian = Boolean(course.typicalMedian);
            return (
              <Link key={course.slug} className="median-course-row" to={`/course/${course.slug}/`}>
                <div className="median-course-copy">
                  <strong>{course.shortName}</strong>
                  <span>{course.title}</span>
                  <small>
                    {course.departmentName || course.department} • {course.legacyReviewCount} review{course.legacyReviewCount === 1 ? "" : "s"}
                    {course.typicalMedianReviewCount
                      ? ` • ${course.typicalMedianReviewCount} reported median${course.typicalMedianReviewCount === 1 ? "" : "s"}`
                      : ""}
                  </small>
                </div>
                <div className="median-course-side">
                  <span className="median-course-badge" data-empty={hasMedian ? "false" : "true"}>
                    {hasMedian ? `Median ${course.typicalMedian}` : "No median yet"}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
        {hasMore ? (
          <div className="results-footer">
            <p className="toolbar-summary">
              Showing {visibleCount} of {medianBoard.rankedCourses.length} current-quarter courses
            </p>
            <button type="button" className="ghost-button" onClick={loadMore}>
              Load more
            </button>
            <div ref={anchorRef} className="results-sentinel" aria-hidden="true" />
          </div>
        ) : null}
      </div>
    </section>
  );
}
